import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './InfluencerModalInstagramDetalle.css';
import DemographicsMapView from './DemographicsMapView';
import CountriesBarChart from './CountriesBarChart';
import Loading from './Loading';

const InfluencerModalInstagramDetalle = ({ influencer, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insights, setInsights] = useState(null);
  const [activeTab, setActiveTab] = useState('followers'); // followers, reach, impressions, plays, comments, latest
  const [activeTimeTab, setActiveTimeTab] = useState('general'); // general, IMAGE, VIDEO, REEL, CAROUSEL_ALBUM
  const [topPostsPage, setTopPostsPage] = useState(0); // 0 = posts 1-5, 1 = posts 6-10
  const [analyzingPosts, setAnalyzingPosts] = useState({}); // { post_id: true/false } para tracking de loading
  const [sentimentModalPost, setSentimentModalPost] = useState(null); // post con modal de sentiment abierto
  const [hideContests, setHideContests] = useState(false); // Toggle para ocultar posts de concursos

  // Estados para modal de comentarios
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [commentsModalData, setCommentsModalData] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);

  // Estados para demografía de seguidores
  const [demographics, setDemographics] = useState(null);
  const [loadingDemographics, setLoadingDemographics] = useState(false);
  const [demographicsError, setDemographicsError] = useState(null);

  // Estados para demografía de audiencia alcanzada (30 días)
  const [reachedDemographics, setReachedDemographics] = useState(null);
  const [loadingReachedDemographics, setLoadingReachedDemographics] = useState(false);
  const [reachedDemographicsError, setReachedDemographicsError] = useState(null);

  // Estados para demografía de audiencia comprometida (30 días)
  const [engagedDemographics, setEngagedDemographics] = useState(null);
  const [loadingEngagedDemographics, setLoadingEngagedDemographics] = useState(false);
  const [engagedDemographicsError, setEngagedDemographicsError] = useState(null);

  // Estados para coordenadas geocodificadas de ciudades
  const [followersCitiesWithCoords, setFollowersCitiesWithCoords] = useState([]);
  const [reachedCitiesWithCoords, setReachedCitiesWithCoords] = useState([]);
  const [engagedCitiesWithCoords, setEngagedCitiesWithCoords] = useState([]);
  const [loadingCoordinates, setLoadingCoordinates] = useState(false);

  useEffect(() => {
    loadInstagramInsights();
  }, [influencer.id]);

  // Geocodificar ciudades de seguidores cuando se cargan los datos
  useEffect(() => {
    if (demographics?.top_cities && demographics.top_cities.length > 0) {
      geocodeCities(demographics.top_cities, setFollowersCitiesWithCoords);
    }
  }, [demographics?.top_cities]);

  // Geocodificar ciudades de audiencia alcanzada cuando se cargan los datos
  useEffect(() => {
    if (reachedDemographics?.top_cities && reachedDemographics.top_cities.length > 0) {
      geocodeCities(reachedDemographics.top_cities, setReachedCitiesWithCoords);
    }
  }, [reachedDemographics?.top_cities]);

  // Geocodificar ciudades de audiencia comprometida cuando se cargan los datos
  useEffect(() => {
    if (engagedDemographics?.top_cities && engagedDemographics.top_cities.length > 0) {
      geocodeCities(engagedDemographics.top_cities, setEngagedCitiesWithCoords);
    }
  }, [engagedDemographics?.top_cities]);

  const loadInstagramInsights = async () => {
    setLoading(true);
    setError(null);

    try {
      // Construir la URL del backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.get(
        `${backendUrl}/instagram/influencer/${influencer.id}/insights/`,
        {
          withCredentials: true
        }
      );

      console.log('📊 Insights recibidos:', response.data);
      setInsights(response.data);

      // Cargar demografía automáticamente si hay instagram_id
      if (response.data?.instagram_id) {
        loadDemographics(response.data.instagram_id);
        loadReachedDemographics(response.data.instagram_id);
        loadEngagedDemographics(response.data.instagram_id);
      }
    } catch (err) {
      console.error('❌ Error cargando insights:', err);
      setError('No se pudieron cargar los insights de Instagram');
    } finally {
      setLoading(false);
    }
  };

  const loadDemographics = async (instagramId) => {
    if (!instagramId) {
      console.warn('⚠️ No se puede cargar demografía sin instagram_id');
      return;
    }

    setLoadingDemographics(true);
    setDemographicsError(null);

    try {
      // Construir la URL del backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.get(
        `${backendUrl}/instagram/api/demographics/`,
        {
          params: { instagram_id: instagramId },
          withCredentials: true
        }
      );

      console.log('👥 Demografía recibida:', response.data);
      setDemographics(response.data);
    } catch (err) {
      console.error('❌ Error cargando demografía:', err);

      // Si hay un error 404, significa que no hay datos demográficos aún
      if (err.response?.status === 404) {
        setDemographicsError('No hay datos demográficos disponibles para esta cuenta');
      } else {
        setDemographicsError(err.response?.data?.error || 'No se pudo cargar la demografía');
      }
    } finally {
      setLoadingDemographics(false);
    }
  };

  const loadReachedDemographics = async (instagramId) => {
    if (!instagramId) {
      console.warn('⚠️ No se puede cargar demografía de audiencia alcanzada sin instagram_id');
      return;
    }

    setLoadingReachedDemographics(true);
    setReachedDemographicsError(null);

    try {
      // Construir la URL del backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.get(
        `${backendUrl}/instagram/api/reached-demographics/`,
        {
          params: { instagram_id: instagramId },
          withCredentials: true
        }
      );

      console.log('📊 Demografía de audiencia alcanzada recibida:', response.data);
      setReachedDemographics(response.data);
    } catch (err) {
      console.error('❌ Error cargando demografía de audiencia alcanzada:', err);

      // Si hay un error 404, significa que no hay datos demográficos aún
      if (err.response?.status === 404) {
        setReachedDemographicsError('No hay datos de audiencia alcanzada para esta cuenta');
      } else {
        setReachedDemographicsError(err.response?.data?.error || 'No se pudo cargar la demografía de audiencia alcanzada');
      }
    } finally {
      setLoadingReachedDemographics(false);
    }
  };

  const loadEngagedDemographics = async (instagramId) => {
    if (!instagramId) {
      console.warn('⚠️ No se puede cargar demografía de audiencia comprometida sin instagram_id');
      return;
    }

    setLoadingEngagedDemographics(true);
    setEngagedDemographicsError(null);

    try {
      // Construir la URL del backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.get(
        `${backendUrl}/instagram/api/engaged-demographics/`,
        {
          params: { instagram_id: instagramId },
          withCredentials: true
        }
      );

      console.log('📊 Demografía de audiencia comprometida recibida:', response.data);
      setEngagedDemographics(response.data);
    } catch (err) {
      console.error('❌ Error cargando demografía de audiencia comprometida:', err);

      // Si hay un error 404, significa que no hay datos demográficos aún
      if (err.response?.status === 404) {
        setEngagedDemographicsError('No hay datos de audiencia comprometida para esta cuenta');
      } else {
        setEngagedDemographicsError(err.response?.data?.error || 'No se pudo cargar la demografía de audiencia comprometida');
      }
    } finally {
      setLoadingEngagedDemographics(false);
    }
  };

  // Geocodificar ciudades y obtener coordenadas
  const geocodeCities = async (cities, setterFunction) => {
    if (!cities || cities.length === 0) {
      console.log('⚠️ No hay ciudades para geocodificar');
      return;
    }

    setLoadingCoordinates(true);
    console.log('🗺️ Iniciando geocodificación de', cities.length, 'ciudades');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      // Preparar ciudades para geocodificación (tomar top 10)
      const citiesToGeocode = cities.slice(0, 10).map(city => ({
        city: city.city,
        country_code: '' // Puede extraerse de demographics si está disponible
      }));

      console.log('📤 Enviando request a:', `${backendUrl}/instagram/api/geocode-cities/`);
      console.log('📤 Ciudades a geocodificar:', citiesToGeocode);

      const response = await axios.post(
        `${backendUrl}/instagram/api/geocode-cities/`,
        { cities: citiesToGeocode }
      );

      console.log('📥 Respuesta recibida:', response.data);

      // Combinar coordenadas con datos de ciudad
      const citiesWithCoords = cities.slice(0, 10).map(city => {
        const geocoded = response.data.results.find(
          r => r.city.toLowerCase() === city.city.toLowerCase()
        );

        if (!geocoded) {
          console.warn(`⚠️ No se encontraron coordenadas para ${city.city}`);
        }

        return {
          ...city,
          latitude: geocoded?.latitude || null,
          longitude: geocoded?.longitude || null,
          formatted_address: geocoded?.formatted_address || ''
        };
      }).filter(city => city.latitude && city.longitude);

      console.log(`✅ Geocodificadas ${citiesWithCoords.length}/${cities.length} ciudades`);
      console.log('🗺️ Ciudades con coordenadas:', citiesWithCoords);

      setterFunction(citiesWithCoords);

    } catch (error) {
      console.error('❌ Error geocodificando ciudades:', error);
      console.error('❌ Detalles del error:', error.response?.data);
      console.error('❌ Status:', error.response?.status);
    } finally {
      setLoadingCoordinates(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Formatear tiempo de última actualización
  const formatLastUpdate = () => {
    if (!insights?._cache_updated_at) return 'Actualizando...';

    const ageSeconds = insights._cache_age_seconds || 0;

    if (ageSeconds < 60) {
      return 'Actualizado hace menos de 1 min';
    }

    const ageMinutes = Math.floor(ageSeconds / 60);

    if (ageMinutes < 60) {
      return `Actualizado hace ${ageMinutes} min`;
    }

    const ageHours = Math.floor(ageMinutes / 60);
    const remainingMinutes = ageMinutes % 60;

    if (ageHours < 24) {
      return remainingMinutes > 0
        ? `Actualizado hace ${ageHours}h ${remainingMinutes}min`
        : `Actualizado hace ${ageHours}h`;
    }

    const ageDays = Math.floor(ageHours / 24);
    return `Actualizado hace ${ageDays} día${ageDays > 1 ? 's' : ''}`;
  };

  // Helper para formatear valores según el tipo de métrica
  const formatMetricValue = (value, key) => {
    // Métricas que son porcentajes
    const percentageMetrics = ['follower_growth_rate', 'churn_rate'];

    if (percentageMetrics.includes(key)) {
      return `${Number(value || 0).toFixed(2)}%`;
    }

    return formatNumber(value);
  };

  // Helper para manejar error de carga de imágenes
  const handleImgError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src =
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600">
           <rect width="100%" height="100%" fill="#f3f4f6"/>
           <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
                 font-family="sans-serif" font-size="16" fill="#9ca3af">sin vista previa</text>
         </svg>`
      );
  };

  const getAudienceData = () => {
    if (!insights?.audience) return null;
    return insights.audience;
  };

  // Helper para obtener TODAS las métricas consolidadas (insights + stats calculadas)
  const getAllAccountMetrics = () => {
    if (!insights) return [];

    // Definir mapping de nombres de métricas a visualización amigable
    const metricLabels = {
      // Métricas de Instagram Insights API
      'impressions': { label: 'Impresiones', icon: '👁️', description: 'Total de vistas del contenido', order: 1 },
      'reach': { label: 'Alcance', icon: '📡', description: 'Cuentas únicas alcanzadas', order: 2 },
      'profile_views_today': { label: 'Visitas al Perfil Hoy', icon: '🌟', description: 'Visitas al perfil de hoy', order: 3 },
      'profile_views': { label: 'Visitas al Perfil', icon: '👤', description: 'Visitas al perfil', order: 4 },
      'profile_activity': { label: 'Actividad Total', icon: '⚡', description: 'Actividad total del perfil', order: 5 },
      'profile_activity_bio_link_clicks': { label: 'Clics en Bio Link', icon: '🔗', description: 'Clics en enlace de la bio', order: 6 },
      'profile_activity_email_contacts': { label: 'Contactos por Email', icon: '📧', description: 'Clics en email', order: 7 },
      'profile_activity_get_directions_clicks': { label: 'Direcciones', icon: '🗺️', description: 'Clics en direcciones', order: 8 },
      'profile_activity_phone_call_clicks': { label: 'Llamadas', icon: '📞', description: 'Clics en llamar', order: 9 },
      'profile_activity_text_message_clicks': { label: 'Mensajes de Texto', icon: '💬', description: 'Clics en mensaje', order: 10 },
      'profile_activity_other_clicks': { label: 'Otros Clics', icon: '👆', description: 'Otros clics en perfil', order: 11 },
      'follower_count': { label: 'Seguidores del Período', icon: '👥', description: 'Total de seguidores', order: 12 },
      'online_followers': { label: 'Seguidores Online', icon: '🟢', description: 'Seguidores activos', order: 13 },
      'website_clicks': { label: 'Clics en Sitio Web', icon: '🌐', description: 'Clics en el sitio web', order: 14 },
      'accounts_engaged': { label: 'Cuentas Comprometidas', icon: '🤝', description: 'Cuentas que interactuaron', order: 15 },
      'follows': { label: 'Nuevos Seguidores', icon: '➕', description: 'Nuevos seguidores (últimos 30 días)', order: 16 },
      'profile_visits': { label: 'Visitas al Perfil', icon: '🚶', description: 'Visitas al perfil', order: 17 },

      // Métricas calculadas del componente (de insights actuales)
      'followers_count': { label: 'Seguidores Actuales', icon: '👥', description: 'Total de seguidores actuales', order: 18 },
      'posts_count': { label: 'Publicaciones', icon: '📸', description: 'Total de posts', order: 19 },
      'likes_count': { label: 'Likes Totales', icon: '❤️', description: 'Likes en últimos posts', order: 20 },
      'comments_count': { label: 'Comentarios Totales', icon: '💬', description: 'Comentarios en últimos posts', order: 21 },
      'total_saved': { label: 'Posts Guardados', icon: '🔖', description: 'Total de guardados', order: 22 },
      'total_shares': { label: 'Compartidos', icon: '🔄', description: 'Total compartidos', order: 23 },
      'follower_growth_rate': { label: 'Tasa de Crecimiento', icon: '📈', description: 'Crecimiento de seguidores (%)', order: 24 },
      'churn_rate': { label: 'Churn Rate', icon: '📉', description: 'Tasa de unfollows (%)', order: 25 },
    };

    // Extraer todas las métricas del objeto insights
    const metrics = [];
    Object.keys(insights).forEach(key => {
      // Filtrar solo métricas numéricas y conocidas (excluir arrays, objetos, etc)
      if (
        typeof insights[key] === 'number' &&
        metricLabels[key] &&
        !key.includes('_daily') &&  // Excluir datos diarios
        !key.includes('_30d')       // Excluir aliases antiguos
      ) {
        metrics.push({
          key,
          value: insights[key],
          ...metricLabels[key]
        });
      }
    });

    // Asegurar que profile_views_today siempre aparezca, incluso si es 0 o undefined
    const hasProfileViewsToday = metrics.some(m => m.key === 'profile_views_today');
    if (!hasProfileViewsToday && metricLabels['profile_views_today']) {
      metrics.push({
        key: 'profile_views_today',
        value: insights['profile_views_today'] || 0,
        ...metricLabels['profile_views_today']
      });
    }

    // Ordenar por el orden definido
    return metrics.sort((a, b) => a.order - b.order);
  };

  // Función para abrir modal de comentarios filtrados por sentimiento
  const openCommentsModal = async (postId, sentimentFilter) => {
    setLoadingComments(true);
    setCommentsModalOpen(true);
    setCommentsModalData({ post_id: postId, filter: sentimentFilter, comments: [] });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      // Obtener comentarios del post desde el backend
      const response = await axios.get(
        `${backendUrl}/instagram/api/comments/`,
        {
          params: {
            instagram_id: insights.instagram_id || influencer.instagram_account?.instagram_id,
            limit_posts: 100,
            limit_comments_per_post: 200,
            analyze: false // No analizar, solo traer
          },
          withCredentials: true
        }
      );

      console.log('📥 Comentarios obtenidos:', response.data);

      // Buscar los comentarios del post específico
      const postComments = response.data.comments_by_post?.[postId]?.comments || [];

      // Filtrar por sentimiento
      const filteredComments = postComments.filter(comment => {
        if (!sentimentFilter || sentimentFilter === 'all') return true;
        return comment.sentiment_label === sentimentFilter;
      });

      setCommentsModalData({
        post_id: postId,
        filter: sentimentFilter,
        comments: filteredComments,
        total_comments: postComments.length
      });

    } catch (err) {
      console.error('❌ Error obteniendo comentarios:', err);
      alert('Error al cargar comentarios. Intenta de nuevo.');
      setCommentsModalOpen(false);
    } finally {
      setLoadingComments(false);
    }
  };

  // Función para analizar sentimientos de un post
  const analyzeSentiment = async (postId) => {
    // Marcar como "analizando"
    setAnalyzingPosts(prev => ({ ...prev, [postId]: true }));

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/content';
      const backendUrl = apiUrl.replace(/\/api(\/content)?\/?$/, '');

      const response = await axios.post(
        `${backendUrl}/instagram/api/post/analyze-sentiment/`,
        {
          instagram_id: insights.instagram_id || influencer.instagram_account?.instagram_id,
          post_id: postId,
          force_refresh: true // Siempre forzar refresh cuando se presiona el botón
        },
        {
          withCredentials: true
        }
      );

      console.log('✅ Análisis completado:', response.data);

      // Mostrar advertencia si el análisis está incompleto
      if (response.data.warning) {
        console.warn('⚠️ Advertencia:', response.data.warning);
        alert(`⚠️ Advertencia:\n\n${response.data.warning}`);
      }

      // Actualizar el post en insights
      setInsights(prevInsights => {
        const updatePostInArray = (posts) => {
          return posts.map(p => {
            if (p.id === postId) {
              return {
                ...p,
                sentiment: {
                  total_comments: response.data.total_comments,
                  positive: response.data.positive,
                  neutral: response.data.neutral,
                  negative: response.data.negative,
                  positive_percentage: response.data.positive_percentage,
                  neutral_percentage: response.data.neutral_percentage,
                  negative_percentage: response.data.negative_percentage,
                  has_sentiment: response.data.has_sentiment,
                  analyzed_at: response.data.analyzed_at
                }
              };
            }
            return p;
          });
        };

        return {
          ...prevInsights,
          engagement_rates: {
            ...prevInsights.engagement_rates,
            top_by_followers: updatePostInArray(prevInsights.engagement_rates?.top_by_followers || []),
            top_by_reach: updatePostInArray(prevInsights.engagement_rates?.top_by_reach || []),
            top_by_impressions: updatePostInArray(prevInsights.engagement_rates?.top_by_impressions || [])
          }
        };
      });

    } catch (err) {
      console.error('❌ Error analizando sentimientos:', err);
      alert('Error al analizar comentarios. Intenta de nuevo.');
    } finally {
      // Quitar estado de loading
      setAnalyzingPosts(prev => ({ ...prev, [postId]: false }));
    }
  };

  // Helper para obtener la lista de posts según el toggle de concursos
  // Usa las listas pre-calculadas del backend para mostrar siempre 10 posts
  const getPostsList = (postsKey) => {
    if (!insights?.engagement_rates) return [];

    if (hideContests) {
      // Usar la lista pre-calculada sin concursos
      const noContestsKey = `${postsKey}_no_contests`;
      return insights.engagement_rates[noContestsKey] || [];
    }

    // Usar la lista original (puede incluir concursos)
    return insights.engagement_rates[postsKey] || [];
  };

  // Helper para renderizar tarjetas de posts
  const renderPostCard = (post, index, erType, erLabel) => {
    if (!post) return null;

    const erValue = erType ? post[erType] : null;
    const isFeatured = erType === 'er_by_reach';
    const isVideo = post.media_type === 'VIDEO' || post.media_type === 'REEL';
    const isContest = post.is_contest === true;

    return (
      <div
        key={post.id}
        className={`instagram-post-card ${isFeatured ? 'featured' : ''} ${isContest ? 'contest' : ''}`}
      >
        <div className={`post-rank-badge ${isFeatured ? 'featured' : ''}`}>#{index + 1}</div>
        {isVideo && <div className="video-badge">🎥</div>}

        <div className="post-header">
          <div className="post-user-info">
            <div className="post-avatar">
              {insights?.profile_picture ? (
                <img src={insights.profile_picture} alt={insights.username} referrerPolicy="no-referrer" />
              ) : (
                <div className="avatar-placeholder">{insights?.username?.charAt(0) || '?'}</div>
              )}
            </div>
            <div className="post-username">{insights?.username || 'Usuario'}</div>
          </div>
        </div>

        <div className="post-image-container">
          {post.media_url && (
            <img
              src={post.media_url}
              alt={post.caption || 'Post'}
              className="post-image"
              referrerPolicy="no-referrer"
              onError={handleImgError}
            />
          )}
        </div>

        <div className="post-engagement">
          <div className="post-actions" style={{ fontSize: '0.7rem', gap: '0.4rem' }}>
            <span className="post-action" style={{ gap: '0.15rem' }}>
              <img
                src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-like.png"
                alt="likes"
                className="post-action-icon"
                style={{ width: '14px', height: '14px' }}
              />
              {formatNumber(post.like_count || 0)}
            </span>
            <span
              className="post-action"
              style={{ gap: '0.15rem', cursor: 'pointer', position: 'relative' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSentimentModalPost(sentimentModalPost?.id === post.id ? null : post);
              }}
              title="Ver análisis de sentimiento"
            >
              <img
                src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-comentario.png"
                alt="comentarios"
                className="post-action-icon"
                style={{ width: '14px', height: '14px' }}
              />
              {formatNumber(post.comments_count || 0)}
              {post.sentiment?.has_sentiment && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#10b981',
                  borderRadius: '50%',
                  border: '1px solid white'
                }} title="Tiene análisis de sentimiento" />
              )}
            </span>
            <span className="post-action" style={{ gap: '0.15rem' }}>
              <img
                src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-guardar.png"
                alt="guardados"
                className="post-action-icon"
                style={{ width: '14px', height: '14px' }}
              />
              {formatNumber(post.saved || 0)}
            </span>
            <span className="post-action" style={{ gap: '0.15rem' }}>
              <img
                src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-compartir.png"
                alt="compartidos"
                className="post-action-icon"
                style={{ width: '14px', height: '14px' }}
              />
              {formatNumber(post.shares || 0)}
            </span>
          </div>
        </div>

        {post.caption && (
          <div className="post-caption">
            <strong>{insights?.username || 'Usuario'}</strong> {post.caption.substring(0, 100)}{post.caption.length > 100 ? '...' : ''}
          </div>
        )}

        <div className="post-metrics" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          {post.reach > 0 && <span style={{ fontSize: '0.75rem' }}> {formatNumber(post.reach)} alcance</span>}
          {erValue !== null && erValue !== undefined && (
            <div className={`post-er-badge ${isFeatured ? 'featured' : ''}`} style={{ margin: 0 }}>
              {Number(erValue).toFixed(2)}% {erLabel}
            </div>
          )}
        </div>

        {/* Modal de Sentiment Analysis */}
        {sentimentModalPost?.id === post.id && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSentimentModalPost(null);
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '1.5rem',
                maxWidth: '400px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del modal */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Análisis de Comentarios</h4>
                <button
                  onClick={() => setSentimentModalPost(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '0',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>

              {/* Contenido de Sentiment */}
              {post.sentiment && post.sentiment.has_sentiment ? (
                <div className="post-sentiment" style={{ border: 'none', padding: 0, margin: 0 }}>
                  <div className="sentiment-header">
                    <span className="sentiment-icon">💬</span>
                    <span className="sentiment-title">Sentimiento de {post.sentiment.positive + post.sentiment.neutral + post.sentiment.negative} comentarios</span>
                  </div>
                  {post.sentiment.analyzed_at && (
                    <div className="sentiment-analyzed-date">
                      Analizado: {new Date(post.sentiment.analyzed_at).toLocaleString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  )}
                  <div className="sentiment-bars">
                    {post.sentiment.positive > 0 && (
                      <div className="sentiment-bar-container">
                        <div
                          className="sentiment-bar positive"
                          style={{ width: `${post.sentiment.positive_percentage}%` }}
                          title={`${post.sentiment.positive} positivos (${post.sentiment.positive_percentage}%)`}
                        >
                          {post.sentiment.positive_percentage >= 15 && (
                            <span className="sentiment-bar-label">😊 {post.sentiment.positive_percentage}%</span>
                          )}
                        </div>
                      </div>
                    )}
                    {post.sentiment.neutral > 0 && (
                      <div className="sentiment-bar-container">
                        <div
                          className="sentiment-bar neutral"
                          style={{ width: `${post.sentiment.neutral_percentage}%` }}
                          title={`${post.sentiment.neutral} neutrales (${post.sentiment.neutral_percentage}%)`}
                        >
                          {post.sentiment.neutral_percentage >= 15 && (
                            <span className="sentiment-bar-label">😐 {post.sentiment.neutral_percentage}%</span>
                          )}
                        </div>
                      </div>
                    )}
                    {post.sentiment.negative > 0 && (
                      <div className="sentiment-bar-container">
                        <div
                          className="sentiment-bar negative"
                          style={{ width: `${post.sentiment.negative_percentage}%` }}
                          title={`${post.sentiment.negative} negativos (${post.sentiment.negative_percentage}%)`}
                        >
                          {post.sentiment.negative_percentage >= 15 && (
                            <span className="sentiment-bar-label">😞 {post.sentiment.negative_percentage}%</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="sentiment-stats">
                    <span
                      className="sentiment-stat positive clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openCommentsModal(post.id, 'positive');
                      }}
                      title="Ver comentarios positivos"
                    >
                      😊 {post.sentiment.positive}
                    </span>
                    <span
                      className="sentiment-stat neutral clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openCommentsModal(post.id, 'neutral');
                      }}
                      title="Ver comentarios neutrales"
                    >
                      😐 {post.sentiment.neutral}
                    </span>
                    <span
                      className="sentiment-stat negative clickable"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openCommentsModal(post.id, 'negative');
                      }}
                      title="Ver comentarios negativos"
                    >
                      😞 {post.sentiment.negative}
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                  <p style={{ margin: '0 0 1rem 0' }}>No hay análisis de sentimiento disponible para este post.</p>
                </div>
              )}

              {/* Botón de Análisis */}
              <div style={{ marginTop: '1rem' }}>
                <button
                  className={`sentiment-analyze-btn ${analyzingPosts[post.id] ? 'loading' : ''} ${post.sentiment?.has_sentiment ? 'update' : 'analyze'}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    analyzeSentiment(post.id);
                  }}
                  disabled={analyzingPosts[post.id]}
                  style={{ width: '100%' }}
                >
                  {analyzingPosts[post.id] ? (
                    <>
                      <span className="btn-spinner"></span>
                      Analizando...
                    </>
                  ) : post.sentiment?.has_sentiment ? (
                    <>
                      🔄 Actualizar Análisis
                    </>
                  ) : (
                    <>
                      💬 Analizar Comentarios
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="instagram-insights-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="insights-header">
          <div className="modal-header-content">
            <div className="instagram-logo">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <defs>
                  <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#833AB4" />
                    <stop offset="50%" stopColor="#FD1D1D" />
                    <stop offset="100%" stopColor="#FCB045" />
                  </linearGradient>
                </defs>
                <rect width="32" height="32" rx="8" fill="url(#instagram-gradient)" />
                <circle cx="16" cy="16" r="6" fill="none" stroke="white" strokeWidth="2" />
                <circle cx="23" cy="9" r="1.5" fill="white" />
              </svg>
            </div>
            <div className="modal-header-text">
              <h2>Instagram Insights</h2>
            </div>
          </div>
          <button onClick={onClose} className="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="insights-content">
          {loading ? (
            <Loading fullScreen={false} />
          ) : error ? (
            <div className="insights-error">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button onClick={loadInstagramInsights} className="btn-retry">
                Reintentar
              </button>
            </div>
          ) : insights ? (
            <>
              {/* Perfil estilo Instagram */}
              <div className="insights-section">
                <div className="instagram-profile-container">
                  {/* Header del Perfil */}
                  <div className="instagram-profile-header">
                    <div className="instagram-profile-avatar">
                      {insights?.profile_picture ? (
                        <img src={insights.profile_picture} alt={insights.username} referrerPolicy="no-referrer" />
                      ) : (
                        <div className="avatar-placeholder-large">{insights?.username?.charAt(0) || '?'}</div>
                      )}
                    </div>
                    <div className="instagram-profile-info">
                      <div className="instagram-username-container">
                        <h2 className="instagram-username">{insights?.username || influencer.username}</h2>
                        <span className="instagram-last-update">
                          🕐 {formatLastUpdate()}
                        </span>
                      </div>
                      <div className="instagram-profile-stats">
                        <div className="profile-stat-item">
                          <span className="profile-stat-value">{formatNumber(insights?.posts_count || 0)}</span>
                          <span className="profile-stat-label">publicaciones</span>
                        </div>
                        <div className="profile-stat-item">
                          <span className="profile-stat-value">{formatNumber(insights?.followers_count || 0)}</span>
                          <span className="profile-stat-label">seguidores</span>
                        </div>
                        <div className="profile-stat-item">
                          <span className="profile-stat-value">{formatNumber(insights?.following_count || 0)}</span>
                          <span className="profile-stat-label">seguidos</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Métricas Principales - Grid de 6 columnas */}
                  <div className="instagram-profile-metrics">
                    <div className="profile-metric-card">
                      <div className="profile-metric-icon">
                        <img
                          src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-like.png"
                          alt="likes"
                          style={{ width: '32px', height: '32px' }}
                        />
                      </div>
                      <div className="profile-metric-info">
                        <div className="profile-metric-value">{formatNumber(insights?.likes_count || 0)}</div>
                      </div>
                    </div>
                    <div className="profile-metric-card">
                      <div className="profile-metric-icon">
                        <img
                          src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-comentario.png"
                          alt="comentarios"
                          style={{ width: '32px', height: '32px' }}
                        />
                      </div>
                      <div className="profile-metric-info">
                        <div className="profile-metric-value">{formatNumber(insights?.comments_count || 0)}</div>
                      </div>
                    </div>
                    <div className="profile-metric-card">
                      <div className="profile-metric-icon">
                        <img
                          src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-guardar.png"
                          alt="guardados"
                          style={{ width: '32px', height: '32px' }}
                        />
                      </div>
                      <div className="profile-metric-info">
                        <div className="profile-metric-value">{formatNumber(insights?.total_saved || 0)}</div>
                      </div>
                    </div>
                    <div className="profile-metric-card">
                      <div className="profile-metric-icon">
                        <img
                          src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-compartir.png"
                          alt="compartidos"
                          style={{ width: '32px', height: '32px' }}
                        />
                      </div>
                      <div className="profile-metric-info">
                        <div className="profile-metric-value">{formatNumber(insights?.total_shares || 0)}</div>
                      </div>
                    </div>
                    <div className="profile-metric-card">
                      <div className="profile-metric-icon">⚡</div>
                      <div className="profile-metric-info">
                        <div className="profile-metric-value">{formatNumber(insights?.total_post_interactions || 0)}</div>
                      </div>
                    </div>
                    {(() => {
                      // Calcular nuevos seguidores del último día vs el anterior
                      const history = insights?.follower_history || [];
                      let diff = 0;
                      let hasDailyData = false;

                      if (history.length >= 2) {
                        const lastDay = history[history.length - 1];
                        const previousDay = history[history.length - 2];
                        diff = lastDay.followers_count - previousDay.followers_count;
                        hasDailyData = true;
                      }

                      return (
                        <div
                          className="profile-metric-card"
                          style={hasDailyData ? {
                            backgroundColor: diff >= 0 ? '#ecfdf5' : '#fef2f2',
                            border: `1px solid ${diff >= 0 ? '#10b981' : '#ef4444'}`
                          } : {}}
                        >
                          <div className="profile-metric-icon"></div>
                          <div className="profile-metric-info">
                            <div
                              className="profile-metric-value"
                              style={hasDailyData ? { color: diff >= 0 ? '#10b981' : '#ef4444' } : {}}
                            >
                              {hasDailyData ? (diff >= 0 ? '+' : '') + diff.toLocaleString() : formatNumber(insights?.new_followers || 0)}
                            </div>
                            <div className="profile-metric-label">Seguidores</div>
                          </div>
                        </div>
                      );
                    })()}
                    {(() => {
                      // Calcular cambio en visitas al perfil vs último día
                      const history = insights?.profile_view_history || [];
                      let diff = 0;
                      let hasDailyData = false;
                      let currentViews = insights?.profile_views || 0;

                      if (history.length >= 2) {
                        const lastDay = history[history.length - 1];
                        const previousDay = history[history.length - 2];
                        diff = lastDay.profile_views - previousDay.profile_views;
                        currentViews = lastDay.profile_views;
                        hasDailyData = true;
                      } else if (history.length === 1) {
                        currentViews = history[0].profile_views;
                        hasDailyData = true;
                      }

                      return (
                        <div
                          className="profile-metric-card"
                          style={hasDailyData && history.length >= 2 ? {
                            backgroundColor: diff >= 0 ? '#ecfdf5' : '#fef2f2',
                            border: `1px solid ${diff >= 0 ? '#10b981' : '#ef4444'}`
                          } : {}}
                        >
                          <div className="profile-metric-icon"></div>
                          <div className="profile-metric-info">
                            <div
                              className="profile-metric-value"
                              style={hasDailyData && history.length >= 2 ? { color: diff >= 0 ? '#10b981' : '#ef4444' } : {}}
                            >
                              {hasDailyData && history.length >= 2 ? (diff >= 0 ? '+' : '') + diff.toLocaleString() : formatNumber(currentViews)}
                            </div>
                            <div className="profile-metric-label">Visitas al Perfil</div>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="profile-metric-card">
                      <div className="profile-metric-icon"></div>
                      <div className="profile-metric-info">
                        <div className="profile-metric-value">{formatNumber(insights?.website_clicks || 0)}</div>
                        <div className="profile-metric-label">Clics en Sitio Web</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de 3 columnas: Seguidores, Visitas al Perfil, Tipos de Contenido */}
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* Columna 1: Historial de Seguidores */}
                {insights.follower_history && insights.follower_history.length > 0 && (
                  <div className="insights-section" style={{ flex: 1, marginBottom: 0 }}>
                    <h3 className="section-title">Seguidores</h3>
                    {(() => {
                      const history = insights.follower_history;
                      const maxFollowers = Math.max(...history.map(d => d.followers_count));
                      const minFollowers = Math.min(...history.map(d => d.followers_count));
                      const range = maxFollowers - minFollowers || 1;
                      const width = 100;
                      const height = 100;

                      const points = history.map((day, index) => {
                        const x = (index / (history.length - 1 || 1)) * (width - 10) + 5;
                        const y = height - (((day.followers_count - minFollowers) / range) * (height - 20) + 10);
                        return { x, y, ...day };
                      });

                      const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                      const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - 5} L ${points[0].x} ${height - 5} Z`;

                      return (
                        <div style={{ backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>Últimos {history.length} días</span>
                            <span style={{ fontSize: '0.7rem', color: '#667eea', fontWeight: '600' }}>{history[history.length - 1]?.followers_count.toLocaleString()}</span>
                          </div>
                          <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '80px' }} preserveAspectRatio="none">
                            <path d={areaPath} fill="url(#followerGradient)" opacity="0.3" />
                            <path d={linePath} fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                            {points.map((p, i) => (
                              <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 3 : 1.5} fill={i === points.length - 1 ? '#667eea' : '#a5b4fc'} stroke="white" strokeWidth="1">
                                <title>{`${p.date}: ${p.followers_count.toLocaleString()}`}</title>
                              </circle>
                            ))}
                            <defs>
                              <linearGradient id="followerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#667eea" />
                                <stop offset="100%" stopColor="#667eea" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                          </svg>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.6rem', color: '#9ca3af' }}>
                            <span>{history[0]?.date}</span>
                            <span>{history[history.length - 1]?.date}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Columna 2: Historial de Visitas al Perfil */}
                {insights.profile_view_history && insights.profile_view_history.length > 0 && (
                  <div className="insights-section" style={{ flex: 1, marginBottom: 0 }}>
                    <h3 className="section-title">Visitas al Perfil</h3>
                    {(() => {
                      const history = insights.profile_view_history;
                      const maxViews = Math.max(...history.map(d => d.profile_views));
                      const minViews = Math.min(...history.map(d => d.profile_views));
                      const range = maxViews - minViews || 1;
                      const width = 100;
                      const height = 100;

                      const points = history.map((day, index) => {
                        const x = (index / (history.length - 1 || 1)) * (width - 10) + 5;
                        const y = height - (((day.profile_views - minViews) / range) * (height - 20) + 10);
                        return { x, y, ...day };
                      });

                      const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
                      const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - 5} L ${points[0].x} ${height - 5} Z`;

                      return (
                        <div style={{ backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>Últimos {history.length} días</span>
                            <span style={{ fontSize: '0.7rem', color: '#10b981', fontWeight: '600' }}>{history[history.length - 1]?.profile_views.toLocaleString()}</span>
                          </div>
                          <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '80px' }} preserveAspectRatio="none">
                            <path d={areaPath} fill="url(#profileViewGradient)" opacity="0.3" />
                            <path d={linePath} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                            {points.map((p, i) => (
                              <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 3 : 1.5} fill={i === points.length - 1 ? '#10b981' : '#6ee7b7'} stroke="white" strokeWidth="1">
                                <title>{`${p.date}: ${p.profile_views.toLocaleString()}`}</title>
                              </circle>
                            ))}
                            <defs>
                              <linearGradient id="profileViewGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                          </svg>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.6rem', color: '#9ca3af' }}>
                            <span>{history[0]?.date}</span>
                            <span>{history[history.length - 1]?.date}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Columna 3: Tipos de Contenido */}
                {insights.content_performance && Object.keys(insights.content_performance).length > 0 && (
                  <div className="insights-section" style={{ flex: 1, marginBottom: 0 }}>
                    <h3 className="section-title">Tipos de Contenido</h3>
                    {(() => {
                      const typeNames = {
                        'IMAGE': { name: 'Imágenes', icon: '🖼️', color: '#8b5cf6' },
                        'VIDEO': { name: 'Videos', icon: '🎬', color: '#ef4444' },
                        'CAROUSEL_ALBUM': { name: 'Carruseles', icon: '📸', color: '#3b82f6' },
                        'REEL': { name: 'Reels', icon: '🎥', color: '#ec4899' }
                      };

                      const sortedData = Object.entries(insights.content_performance)
                        .sort((a, b) => (b[1].avg_engagement_rate || 0) - (a[1].avg_engagement_rate || 0));

                      const maxER = Math.max(...sortedData.map(([, data]) => data.avg_engagement_rate || 0));

                      return (
                        <div style={{ backgroundColor: '#f9fafb', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: '120px', justifyContent: 'center' }}>
                            {sortedData.map(([type, data], index) => {
                              const typeInfo = typeNames[type] || { name: type, icon: '📄', color: '#6b7280' };
                              const erValue = data.avg_engagement_rate || 0;
                              const barWidth = maxER > 0 ? (erValue / maxER) * 100 : 0;

                              return (
                                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '0.75rem', width: '70px', fontWeight: '500', color: '#374151' }}>{typeInfo.name}</span>
                                  <div style={{ flex: 1, height: '20px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                                    <div style={{
                                      width: `${barWidth}%`,
                                      height: '100%',
                                      backgroundColor: typeInfo.color,
                                      borderRadius: '4px',
                                      transition: 'width 0.5s ease-out',
                                      opacity: 0.7
                                    }} />
                                    <span style={{
                                      position: 'absolute',
                                      right: '0.5rem',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      fontSize: '0.65rem',
                                      fontWeight: '600',
                                      color: '#374151'
                                    }}>
                                      {erValue.toFixed(1)}% ({data.post_count})
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Métricas por tipo de post: Regular vs Concurso */}
              {insights.post_type_metrics && (
                <div className="insights-section">
                  <h3 className="section-title">Métricas por Tipo de Post</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                    {/* Posts Regulares */}
                    <div style={{
                      backgroundColor: '#f0fdf4',
                      borderRadius: '12px',
                      padding: '1rem',
                      border: '1px solid #bbf7d0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: '600', color: '#166534' }}>Posts Regulares</span>
                        <span style={{
                          backgroundColor: '#22c55e',
                          color: 'white',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {insights.post_type_metrics.regular?.count || 0}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', fontSize: '0.8rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-like.png" alt="likes" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Likes:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.regular?.avg_likes || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-comentario.png" alt="comments" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Comments:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.regular?.avg_comments || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-guardar.png" alt="saved" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Guardados:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.regular?.avg_saved || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-compartir.png" alt="shares" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Compartidos:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.regular?.avg_shares || 0)}</span>
                        </div>
                      </div>
                      <div style={{
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid #bbf7d0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>ER Promedio:</span>
                        <span style={{
                          backgroundColor: '#22c55e',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontWeight: '700',
                          fontSize: '0.9rem'
                        }}>
                          {(insights.post_type_metrics.regular?.avg_engagement_rate || 0).toFixed(2)}%
                        </span>
                      </div>
                    </div>

                    {/* Posts de Concurso */}
                    <div style={{
                      backgroundColor: '#fef3c7',
                      borderRadius: '12px',
                      padding: '1rem',
                      border: '1px solid #fcd34d'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <span style={{ fontWeight: '600', color: '#92400e' }}>Posts de Concurso</span>
                        <span style={{
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '9999px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {insights.post_type_metrics.contest?.count || 0}
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', fontSize: '0.8rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-like.png" alt="likes" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Likes:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.contest?.avg_likes || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-comentario.png" alt="comments" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Comments:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.contest?.avg_comments || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-guardar.png" alt="saved" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Guardados:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.contest?.avg_saved || 0)}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <img src="https://gevesmaicaobucket.fra1.cdn.digitaloceanspaces.com/media/instagram-iconos/icono-compartir.png" alt="shares" style={{ width: '16px', height: '16px' }} />
                          <span style={{ color: '#6b7280' }}>Compartidos:</span>
                          <span style={{ fontWeight: '600' }}>{formatNumber(insights.post_type_metrics.contest?.avg_shares || 0)}</span>
                        </div>
                      </div>
                      <div style={{
                        marginTop: '0.75rem',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid #fcd34d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>ER Promedio:</span>
                        <span style={{
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px',
                          fontWeight: '700',
                          fontSize: '0.9rem'
                        }}>
                          {(insights.post_type_metrics.contest?.avg_engagement_rate || 0).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tabbed Top Posts Section */}
              {insights.engagement_rates && insights.engagement_rates.averages && (
                <div className="insights-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 className="section-title" style={{ margin: 0 }}>Top Posts</h3>

                    {/* Toggle para ocultar concursos */}
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#374151',
                      userSelect: 'none'
                    }}>
                      <span>Ocultar concursos</span>
                      <div
                        onClick={() => { setHideContests(!hideContests); setTopPostsPage(0); }}
                        style={{
                          width: '44px',
                          height: '24px',
                          backgroundColor: hideContests ? '#f59e0b' : '#d1d5db',
                          borderRadius: '9999px',
                          position: 'relative',
                          transition: 'background-color 0.2s',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{
                          position: 'absolute',
                          top: '2px',
                          left: hideContests ? '22px' : '2px',
                          width: '20px',
                          height: '20px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                    </label>
                  </div>

                  {/* Tabs */}
                  <div className="instagram-tabs">
                    <button
                      className={activeTab === 'followers' ? 'active' : ''}
                      onClick={() => { setActiveTab('followers'); setTopPostsPage(0); }}
                    >
                      Seguidores
                    </button>
                    <button
                      className={activeTab === 'reach' ? 'active' : ''}
                      onClick={() => { setActiveTab('reach'); setTopPostsPage(0); }}
                    >
                      Alcance
                    </button>
                    <button
                      className={activeTab === 'impressions' ? 'active' : ''}
                      onClick={() => { setActiveTab('impressions'); setTopPostsPage(0); }}
                    >
                      Impresiones
                    </button>
                  </div>

                  {/* Tab Content */}
                  {/* ER por Seguidores */}
                  {activeTab === 'followers' && (
                    <>
                      {(() => {
                        const postsList = getPostsList('top_by_followers');
                        return postsList.length > 0 ? (
                          <>
                            <div className="top-posts-grid">
                              {postsList.slice(topPostsPage * 5, topPostsPage * 5 + 5).map((post, index) =>
                                renderPostCard(post, topPostsPage * 5 + index, 'er_by_followers', 'ER')
                              )}
                            </div>
                            {postsList.length > 5 && (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                                <button
                                  onClick={() => setTopPostsPage(0)}
                                  disabled={topPostsPage === 0}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: topPostsPage === 0 ? '#e5e7eb' : '#3b82f6',
                                    color: topPostsPage === 0 ? '#9ca3af' : 'white',
                                    cursor: topPostsPage === 0 ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  1-5
                                </button>
                                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  {topPostsPage === 0 ? '1-5' : '6-10'} de {postsList.length}
                                  {hideContests && <span style={{ color: '#f59e0b', marginLeft: '0.25rem' }}>(sin concursos)</span>}
                                </span>
                                <button
                                  onClick={() => setTopPostsPage(1)}
                                  disabled={topPostsPage === 1 || postsList.length <= 5}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: topPostsPage === 1 || postsList.length <= 5 ? '#e5e7eb' : '#3b82f6',
                                    color: topPostsPage === 1 || postsList.length <= 5 ? '#9ca3af' : 'white',
                                    cursor: topPostsPage === 1 || postsList.length <= 5 ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  6-10
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="empty-state">
                            {hideContests
                              ? 'No hay posts regulares (sin concursos) con datos suficientes'
                              : 'No hay posts con datos suficientes para calcular ER por seguidores'}
                          </p>
                        );
                      })()}
                    </>
                  )}

                  {/* ER por Alcance */}
                  {activeTab === 'reach' && (
                    <>
                      {(() => {
                        const postsList = getPostsList('top_by_reach');
                        return postsList.length > 0 ? (
                          <>
                            <div className="top-posts-grid">
                              {postsList.slice(topPostsPage * 5, topPostsPage * 5 + 5).map((post, index) =>
                                renderPostCard(post, topPostsPage * 5 + index, 'er_by_reach', 'ER')
                              )}
                            </div>
                            {postsList.length > 5 && (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                                <button
                                  onClick={() => setTopPostsPage(0)}
                                  disabled={topPostsPage === 0}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: topPostsPage === 0 ? '#e5e7eb' : '#3b82f6',
                                    color: topPostsPage === 0 ? '#9ca3af' : 'white',
                                    cursor: topPostsPage === 0 ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  1-5
                                </button>
                                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  {topPostsPage === 0 ? '1-5' : '6-10'} de {postsList.length}
                                  {hideContests && <span style={{ color: '#f59e0b', marginLeft: '0.25rem' }}>(sin concursos)</span>}
                                </span>
                                <button
                                  onClick={() => setTopPostsPage(1)}
                                  disabled={topPostsPage === 1 || postsList.length <= 5}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: topPostsPage === 1 || postsList.length <= 5 ? '#e5e7eb' : '#3b82f6',
                                    color: topPostsPage === 1 || postsList.length <= 5 ? '#9ca3af' : 'white',
                                    cursor: topPostsPage === 1 || postsList.length <= 5 ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  6-10
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="empty-state">
                            {hideContests
                              ? 'No hay posts regulares (sin concursos) con datos de alcance'
                              : 'No hay posts con datos de alcance. Los datos de alcance requieren que la cuenta tenga acceso completo a Instagram Insights API.'}
                          </p>
                        );
                      })()}
                    </>
                  )}

                  {/* ER por Impresiones */}
                  {activeTab === 'impressions' && (
                    <>
                      {(() => {
                        const postsList = getPostsList('top_by_impressions');
                        return postsList.length > 0 ? (
                          <>
                            <div className="top-posts-grid">
                              {postsList.slice(topPostsPage * 5, topPostsPage * 5 + 5).map((post, index) =>
                                renderPostCard(post, topPostsPage * 5 + index, 'er_by_impressions', 'ER')
                              )}
                            </div>
                            {postsList.length > 5 && (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                                <button
                                  onClick={() => setTopPostsPage(0)}
                                  disabled={topPostsPage === 0}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: topPostsPage === 0 ? '#e5e7eb' : '#3b82f6',
                                    color: topPostsPage === 0 ? '#9ca3af' : 'white',
                                    cursor: topPostsPage === 0 ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  1-5
                                </button>
                                <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                  {topPostsPage === 0 ? '1-5' : '6-10'} de {postsList.length}
                                  {hideContests && <span style={{ color: '#f59e0b', marginLeft: '0.25rem' }}>(sin concursos)</span>}
                                </span>
                                <button
                                  onClick={() => setTopPostsPage(1)}
                                  disabled={topPostsPage === 1 || postsList.length <= 5}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: topPostsPage === 1 || postsList.length <= 5 ? '#e5e7eb' : '#3b82f6',
                                    color: topPostsPage === 1 || postsList.length <= 5 ? '#9ca3af' : 'white',
                                    cursor: topPostsPage === 1 || postsList.length <= 5 ? 'not-allowed' : 'pointer',
                                    fontWeight: '500',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  6-10
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="empty-state">
                            {hideContests
                              ? 'No hay posts regulares (sin concursos) con datos de impresiones'
                              : 'No hay posts con datos de impresiones. Los datos de impresiones requieren que la cuenta tenga acceso completo a Instagram Insights API.'}
                          </p>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}

              {/* 6. Mejores Horarios para Publicar (de esta cuenta específica) */}
              {insights.posting_times_analysis && (
                <div className="insights-section">
                  <h3 className="section-title">Mejores Horarios</h3>


                  {/* Tabs */}
                  <div className="instagram-tabs">
                    <button
                      className={activeTimeTab === 'general' ? 'active' : ''}
                      onClick={() => setActiveTimeTab('general')}
                    >
                      General
                    </button>
                    {insights.posting_times_analysis.by_content_type?.IMAGE && insights.posting_times_analysis.by_content_type.IMAGE.length > 0 && (
                      <button
                        className={activeTimeTab === 'IMAGE' ? 'active' : ''}
                        onClick={() => setActiveTimeTab('IMAGE')}
                      >
                        Imágenes
                      </button>
                    )}
                    {insights.posting_times_analysis.by_content_type?.VIDEO && insights.posting_times_analysis.by_content_type.VIDEO.length > 0 && (
                      <button
                        className={activeTimeTab === 'VIDEO' ? 'active' : ''}
                        onClick={() => setActiveTimeTab('VIDEO')}
                      >
                        Videos
                      </button>
                    )}
                    {insights.posting_times_analysis.by_content_type?.CAROUSEL_ALBUM && insights.posting_times_analysis.by_content_type.CAROUSEL_ALBUM.length > 0 && (
                      <button
                        className={activeTimeTab === 'CAROUSEL_ALBUM' ? 'active' : ''}
                        onClick={() => setActiveTimeTab('CAROUSEL_ALBUM')}
                      >
                        Carruseles
                      </button>
                    )}
                  </div>

                  {/* Tab Content - General */}
                  {activeTimeTab === 'general' && (
                    <>
                      {insights.posting_times_analysis.top_general && insights.posting_times_analysis.top_general.length > 0 ? (
                        <div className="posting-times-grid">
                          {insights.posting_times_analysis.top_general.map((time, index) => (
                            <div key={index} className="time-card">
                              <div className="time-rank">#{index + 1}</div>
                              <div className="time-icon"></div>
                              <div className="time-info">
                                <div className="time-value">{time.time}</div>
                                <div className="time-label">{time.day}</div>
                                <div className="time-engagement">{Number(time.engagement_rate || 0).toFixed(2)}% ER</div>
                                {time.post_count && (
                                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>
                                    {time.post_count} {time.post_count === 1 ? 'post' : 'posts'}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="empty-state">No hay datos suficientes para calcular mejores horarios</p>
                      )}
                    </>
                  )}

                  {/* Tab Content - IMAGE */}
                  {activeTimeTab === 'IMAGE' && (
                    <>
                      {insights.posting_times_analysis.by_content_type?.IMAGE && insights.posting_times_analysis.by_content_type.IMAGE.length > 0 ? (
                        <div className="posting-times-grid">
                          {insights.posting_times_analysis.by_content_type.IMAGE.map((time, index) => (
                            <div key={index} className="time-card">
                              <div className="time-rank">#{index + 1}</div>
                              <div className="time-icon"></div>
                              <div className="time-info">
                                <div className="time-value">{time.time}</div>
                                <div className="time-label">{time.day}</div>
                                <div className="time-engagement">{Number(time.engagement_rate || 0).toFixed(2)}% ER</div>
                                {time.post_count && (
                                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>
                                    {time.post_count} {time.post_count === 1 ? 'imagen' : 'imágenes'}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="empty-state">No hay datos suficientes para calcular mejores horarios para imágenes</p>
                      )}
                    </>
                  )}

                  {/* Tab Content - VIDEO */}
                  {activeTimeTab === 'VIDEO' && (
                    <>
                      {insights.posting_times_analysis.by_content_type?.VIDEO && insights.posting_times_analysis.by_content_type.VIDEO.length > 0 ? (
                        <div className="posting-times-grid">
                          {insights.posting_times_analysis.by_content_type.VIDEO.map((time, index) => (
                            <div key={index} className="time-card">
                              <div className="time-rank">#{index + 1}</div>
                              <div className="time-icon"></div>
                              <div className="time-info">
                                <div className="time-value">{time.time}</div>
                                <div className="time-label">{time.day}</div>
                                <div className="time-engagement">{Number(time.engagement_rate || 0).toFixed(2)}% ER</div>
                                {time.post_count && (
                                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>
                                    {time.post_count} {time.post_count === 1 ? 'video' : 'videos'}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="empty-state">No hay datos suficientes para calcular mejores horarios para videos</p>
                      )}
                    </>
                  )}

                  {/* Tab Content - CAROUSEL_ALBUM */}
                  {activeTimeTab === 'CAROUSEL_ALBUM' && (
                    <>
                      {insights.posting_times_analysis.by_content_type?.CAROUSEL_ALBUM && insights.posting_times_analysis.by_content_type.CAROUSEL_ALBUM.length > 0 ? (
                        <div className="posting-times-grid">
                          {insights.posting_times_analysis.by_content_type.CAROUSEL_ALBUM.map((time, index) => (
                            <div key={index} className="time-card">
                              <div className="time-rank">#{index + 1}</div>
                              <div className="time-icon"></div>
                              <div className="time-info">
                                <div className="time-value">{time.time}</div>
                                <div className="time-label">{time.day}</div>
                                <div className="time-engagement">{Number(time.engagement_rate || 0).toFixed(2)}% ER</div>
                                {time.post_count && (
                                  <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '4px' }}>
                                    {time.post_count} {time.post_count === 1 ? 'carrusel' : 'carruseles'}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="empty-state">No hay datos suficientes para calcular mejores horarios para carruseles</p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* 7. Demografía de la Audiencia */}
              <div className="insights-section">
                <h3 className="section-title">👥 Demografía de la Audiencia</h3>

                {loadingDemographics && !demographics && (
                  <Loading fullScreen={false} />
                )}

                {demographicsError && !demographics && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '0.5rem',
                    color: '#991b1b',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      ⚠️ {demographicsError}
                    </p>
                    {insights?.instagram_id && (
                      <button
                        onClick={() => loadDemographics(insights.instagram_id)}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.8125rem',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: 'pointer'
                        }}
                      >
                        🔄 Reintentar
                      </button>
                    )}
                  </div>
                )}

                {demographics && (
                  <>
                    {/* Información del snapshot */}
                    {demographics.snapshot_date && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: '1rem',
                        fontStyle: 'italic'
                      }}>
                        📅 Datos actualizados: {new Date(demographics.snapshot_date).toLocaleDateString('es-CL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    )}

                    {/* Gender, Age Groups, and Countries - Three columns */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      {/* Gender Split */}
                      {demographics.gender_distribution && demographics.gender_distribution.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">Género</h4>
                          <div className="countries-bar-chart">
                            <div className="bars-container">
                              {(() => {
                                const total = demographics.gender_distribution.reduce((sum, g) => sum + g.count, 0);
                                const maxCount = Math.max(...demographics.gender_distribution.map(g => g.count));
                                const barColor = '#667eea'; // Same as followers countries
                                const genderIcons = {
                                  'Hombre': '👨',
                                  'Mujer': '👩',
                                  'No especificado': '❓'
                                };

                                return demographics.gender_distribution
                                  .sort((a, b) => b.count - a.count)
                                  .map((item, index) => {
                                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                    const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                                    const icon = genderIcons[item.gender] || '👤';

                                    return (
                                      <div key={index} className="country-bar-item-row">
                                        <div className="country-info-compact">
                                          <div className="country-rank-small">#{index + 1}</div>
                                          <div className="country-flag-small">{icon}</div>
                                          <div className="country-code">{item.gender}</div>
                                        </div>
                                        <div className="bar-wrapper-row">
                                          <div
                                            className="bar-fill"
                                            style={{
                                              width: `${barWidth}%`,
                                              backgroundColor: barColor,
                                              opacity: 0.2 + (barWidth / 100) * 0.6
                                            }}
                                          >
                                            <div className="bar-gradient" style={{
                                              background: `linear-gradient(90deg, transparent, ${barColor})`
                                            }}></div>
                                          </div>
                                          <div className="bar-label-row">
                                            <span className="percentage">{percentage.toFixed(1)}%</span>
                                            <span className="count">({item.count.toLocaleString()})</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Age Groups */}
                      {demographics.age_distribution && demographics.age_distribution.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">Edades</h4>
                          <div className="countries-bar-chart">
                            <div className="bars-container">
                              {(() => {
                                const total = demographics.age_distribution.reduce((sum, a) => sum + a.count, 0);
                                const maxCount = Math.max(...demographics.age_distribution.map(a => a.count));
                                const barColor = '#667eea'; // Same as followers countries

                                return demographics.age_distribution
                                  .sort((a, b) => b.count - a.count)
                                  .slice(0, 5)
                                  .map((item, index) => {
                                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                    const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;

                                    return (
                                      <div key={index} className="country-bar-item-row">
                                        <div className="country-info-compact">
                                          <div className="country-rank-small">#{index + 1}</div>
                                          <div className="country-code">{item.age_range}</div>
                                        </div>
                                        <div className="bar-wrapper-row">
                                          <div
                                            className="bar-fill"
                                            style={{
                                              width: `${barWidth}%`,
                                              backgroundColor: barColor,
                                              opacity: 0.2 + (barWidth / 100) * 0.6
                                            }}
                                          >
                                            <div className="bar-gradient" style={{
                                              background: `linear-gradient(90deg, transparent, ${barColor})`
                                            }}></div>
                                          </div>
                                          <div className="bar-label-row">
                                            <span className="percentage">{percentage.toFixed(1)}%</span>
                                            <span className="count">({item.count.toLocaleString()})</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Countries Distribution */}
                      {demographics.top_countries && demographics.top_countries.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">🌍 Paises</h4>
                          <CountriesBarChart
                            countries={demographics.top_countries.slice(0, 10)}
                            totalCount={demographics.top_countries.reduce((sum, c) => sum + c.count, 0)}
                            type="followers"
                          />
                        </div>
                      )}
                    </div>

                    {/* Top Cities with Map */}
                    {demographics.top_cities && demographics.top_cities.length > 0 && (
                      <div className="demographic-card">
                        <h4 className="demographic-title">Ciudades</h4>

                        <div className="demographic-split-view">
                          {/* Left: List view */}
                          <div className="demographic-list-section">
                            <div className="cities-list">
                              {demographics.top_cities.slice(0, 10).map((item, index) => {
                                const total = demographics.top_cities.reduce((sum, c) => sum + c.count, 0);
                                const percentage = total > 0 ? (item.count / total) * 100 : 0;

                                return (
                                  <div key={index} className="city-item">
                                    <div className="city-rank">#{index + 1}</div>
                                    <div className="city-name">
                                       {item.city}
                                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                                        ({item.count.toLocaleString()} seguidores)
                                      </span>
                                    </div>
                                    <div className="city-percentage">{percentage.toFixed(1)}%</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Center: Cities Map view */}
                          <div className="demographic-map-section">
                            <DemographicsMapView
                              cities={followersCitiesWithCoords}
                              totalCount={demographics.top_cities.reduce((sum, c) => sum + c.count, 0)}
                              type="followers"
                              isLoading={loadingCoordinates}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mensaje si no hay datos completos */}
                    {!demographics.has_complete_data && demographics.fetch_status === 'partial' && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e',
                        marginTop: '1rem'
                      }}>
                        ⚠️ Algunos datos demográficos no están disponibles para esta cuenta.
                      </div>
                    )}
                  </>
                )}

                {!demographics && !loadingDemographics && !demographicsError && (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.5rem',
                    color: '#6b7280'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      No hay datos demográficos disponibles.
                    </p>
                    {insights?.instagram_id && (
                      <button
                        onClick={() => loadDemographics(insights.instagram_id)}
                        style={{
                          marginTop: '1rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        📊 Obtener Demografía
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* 8. Demografía de Audiencia Alcanzada (últimos 30 días) */}
              <div className="insights-section" style={{ marginTop: '2rem' }}>
                <h3 className="section-title">Demografía de Audiencia Alcanzada (Mes actual)</h3>

                {loadingReachedDemographics && !reachedDemographics && (
                  <Loading fullScreen={false} />
                )}

                {reachedDemographicsError && !reachedDemographics && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '0.5rem',
                    color: '#991b1b',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      ⚠️ {reachedDemographicsError}
                    </p>
                    {insights?.instagram_id && (
                      <button
                        onClick={() => loadReachedDemographics(insights.instagram_id)}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.8125rem',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: 'pointer'
                        }}
                      >
                        🔄 Reintentar
                      </button>
                    )}
                  </div>
                )}

                {reachedDemographics && (
                  <>
                    {/* Información del snapshot */}
                    {reachedDemographics.snapshot_date && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: '1rem',
                        fontStyle: 'italic'
                      }}>
                        📅 Datos actualizados: {new Date(reachedDemographics.snapshot_date).toLocaleDateString('es-CL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    )}

                    {/* Gender, Age Groups, and Countries - Three columns */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      {/* Gender Split */}
                      {reachedDemographics.gender_distribution && reachedDemographics.gender_distribution.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">Géneros</h4>
                          <div className="countries-bar-chart">
                            <div className="bars-container">
                              {(() => {
                                const total = reachedDemographics.gender_distribution.reduce((sum, g) => sum + g.count, 0);
                                const maxCount = Math.max(...reachedDemographics.gender_distribution.map(g => g.count));
                                const barColor = '#10b981'; // Same as reached countries (green)
                                const genderIcons = {
                                  'Hombre': '👨',
                                  'Mujer': '👩',
                                  'No especificado': '❓'
                                };

                                return reachedDemographics.gender_distribution
                                  .sort((a, b) => b.count - a.count)
                                  .map((item, index) => {
                                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                    const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                                    const icon = genderIcons[item.gender] || '👤';

                                    return (
                                      <div key={index} className="country-bar-item-row">
                                        <div className="country-info-compact">
                                          <div className="country-rank-small">#{index + 1}</div>
                                          <div className="country-flag-small">{icon}</div>
                                          <div className="country-code">{item.gender}</div>
                                        </div>
                                        <div className="bar-wrapper-row">
                                          <div
                                            className="bar-fill"
                                            style={{
                                              width: `${barWidth}%`,
                                              backgroundColor: barColor,
                                              opacity: 0.2 + (barWidth / 100) * 0.6
                                            }}
                                          >
                                            <div className="bar-gradient" style={{
                                              background: `linear-gradient(90deg, transparent, ${barColor})`
                                            }}></div>
                                          </div>
                                          <div className="bar-label-row">
                                            <span className="percentage">{percentage.toFixed(1)}%</span>
                                            <span className="count">({item.count.toLocaleString()})</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Age Groups */}
                      {reachedDemographics.age_distribution && reachedDemographics.age_distribution.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">Edades</h4>
                          <div className="countries-bar-chart">
                            <div className="bars-container">
                              {(() => {
                                const total = reachedDemographics.age_distribution.reduce((sum, a) => sum + a.count, 0);
                                const maxCount = Math.max(...reachedDemographics.age_distribution.map(a => a.count));
                                const barColor = '#10b981'; // Same as reached countries (green)

                                return reachedDemographics.age_distribution
                                  .sort((a, b) => b.count - a.count)
                                  .slice(0, 5)
                                  .map((item, index) => {
                                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                    const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;

                                    return (
                                      <div key={index} className="country-bar-item-row">
                                        <div className="country-info-compact">
                                          <div className="country-rank-small">#{index + 1}</div>
                                          <div className="country-code">{item.age_range}</div>
                                        </div>
                                        <div className="bar-wrapper-row">
                                          <div
                                            className="bar-fill"
                                            style={{
                                              width: `${barWidth}%`,
                                              backgroundColor: barColor,
                                              opacity: 0.2 + (barWidth / 100) * 0.6
                                            }}
                                          >
                                            <div className="bar-gradient" style={{
                                              background: `linear-gradient(90deg, transparent, ${barColor})`
                                            }}></div>
                                          </div>
                                          <div className="bar-label-row">
                                            <span className="percentage">{percentage.toFixed(1)}%</span>
                                            <span className="count">({item.count.toLocaleString()})</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Countries Distribution */}
                      {reachedDemographics.top_countries && reachedDemographics.top_countries.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">🌍 Paises</h4>
                          <CountriesBarChart
                            countries={reachedDemographics.top_countries.slice(0, 10)}
                            totalCount={reachedDemographics.top_countries.reduce((sum, c) => sum + c.count, 0)}
                            type="reached"
                          />
                        </div>
                      )}
                    </div>

                    {/* Top Cities with Map */}
                    {reachedDemographics.top_cities && reachedDemographics.top_cities.length > 0 && (
                      <div className="demographic-card">
                        <h4 className="demographic-title">Ciudades</h4>

                        <div className="demographic-split-view">
                          {/* Left: List view */}
                          <div className="demographic-list-section">
                            <div className="cities-list">
                              {reachedDemographics.top_cities.slice(0, 10).map((item, index) => {
                                const total = reachedDemographics.top_cities.reduce((sum, c) => sum + c.count, 0);
                                const percentage = total > 0 ? (item.count / total) * 100 : 0;

                                return (
                                  <div key={index} className="city-item">
                                    <div className="city-rank">#{index + 1}</div>
                                    <div className="city-name">
                                       {item.city}
                                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                                        ({item.count.toLocaleString()} personas alcanzadas)
                                      </span>
                                    </div>
                                    <div className="city-percentage">{percentage.toFixed(1)}%</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Center: Cities Map view */}
                          <div className="demographic-map-section">
                            <DemographicsMapView
                              cities={reachedCitiesWithCoords}
                              totalCount={reachedDemographics.top_cities.reduce((sum, c) => sum + c.count, 0)}
                              type="reached"
                              isLoading={loadingCoordinates}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mensaje si no hay datos completos */}
                    {!reachedDemographics.has_complete_data && reachedDemographics.fetch_status === 'partial' && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e',
                        marginTop: '1rem'
                      }}>
                        ⚠️ Algunos datos de audiencia alcanzada no están disponibles para esta cuenta.
                      </div>
                    )}
                  </>
                )}

                {!reachedDemographics && !loadingReachedDemographics && !reachedDemographicsError && (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.5rem',
                    color: '#6b7280'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      No hay datos de audiencia alcanzada disponibles.
                    </p>
                    {insights?.instagram_id && (
                      <button
                        onClick={() => loadReachedDemographics(insights.instagram_id)}
                        style={{
                          marginTop: '1rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        📊 Obtener Demografía Alcanzada
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* 9. Demografía de Audiencia Comprometida */}
              <div className="insights-section">
                <h3 className="section-title">Demografía de Audiencia Interacciones (Mes actual)</h3>

                {/* Info text */}


                {loadingEngagedDemographics && !engagedDemographics && (
                  <Loading fullScreen={false} />
                )}

                {engagedDemographicsError && !engagedDemographics && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '0.5rem',
                    color: '#991b1b',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      ⚠️ {engagedDemographicsError}
                    </p>
                    {insights?.instagram_id && (
                      <button
                        onClick={() => loadEngagedDemographics(insights.instagram_id)}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.375rem 0.75rem',
                          fontSize: '0.8125rem',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: 'pointer'
                        }}
                      >
                        🔄 Reintentar
                      </button>
                    )}
                  </div>
                )}

                {engagedDemographics && (
                  <>
                    {/* Información del snapshot */}
                    {engagedDemographics.snapshot_date && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginBottom: '1rem',
                        fontStyle: 'italic'
                      }}>
                        📅 Datos actualizados: {new Date(engagedDemographics.snapshot_date).toLocaleDateString('es-CL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    )}

                    {/* Gender, Age Groups, and Countries - Three columns */}
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      {/* Gender Split */}
                      {engagedDemographics.gender_distribution && engagedDemographics.gender_distribution.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">Géneros</h4>
                          <div className="countries-bar-chart">
                            <div className="bars-container">
                              {(() => {
                                const total = engagedDemographics.gender_distribution.reduce((sum, g) => sum + g.count, 0);
                                const maxCount = Math.max(...engagedDemographics.gender_distribution.map(g => g.count));
                                const barColor = '#f59e0b'; // Same as engaged countries (orange)
                                const genderIcons = {
                                  'Hombre': '👨',
                                  'Mujer': '👩',
                                  'No especificado': '❓'
                                };

                                return engagedDemographics.gender_distribution
                                  .sort((a, b) => b.count - a.count)
                                  .map((item, index) => {
                                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                    const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
                                    const icon = genderIcons[item.gender] || '👤';

                                    return (
                                      <div key={index} className="country-bar-item-row">
                                        <div className="country-info-compact">
                                          <div className="country-rank-small">#{index + 1}</div>
                                          <div className="country-flag-small">{icon}</div>
                                          <div className="country-code">{item.gender}</div>
                                        </div>
                                        <div className="bar-wrapper-row">
                                          <div
                                            className="bar-fill"
                                            style={{
                                              width: `${barWidth}%`,
                                              backgroundColor: barColor,
                                              opacity: 0.2 + (barWidth / 100) * 0.6
                                            }}
                                          >
                                            <div className="bar-gradient" style={{
                                              background: `linear-gradient(90deg, transparent, ${barColor})`
                                            }}></div>
                                          </div>
                                          <div className="bar-label-row">
                                            <span className="percentage">{percentage.toFixed(1)}%</span>
                                            <span className="count">({item.count.toLocaleString()})</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Age Groups */}
                      {engagedDemographics.age_distribution && engagedDemographics.age_distribution.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">Edades</h4>
                          <div className="countries-bar-chart">
                            <div className="bars-container">
                              {(() => {
                                const total = engagedDemographics.age_distribution.reduce((sum, a) => sum + a.count, 0);
                                const maxCount = Math.max(...engagedDemographics.age_distribution.map(a => a.count));
                                const barColor = '#f59e0b'; // Same as engaged countries (orange)

                                return engagedDemographics.age_distribution
                                  .sort((a, b) => b.count - a.count)
                                  .slice(0, 5)
                                  .map((item, index) => {
                                    const percentage = total > 0 ? (item.count / total) * 100 : 0;
                                    const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;

                                    return (
                                      <div key={index} className="country-bar-item-row">
                                        <div className="country-info-compact">
                                          <div className="country-rank-small">#{index + 1}</div>
                                          <div className="country-code">{item.age_range}</div>
                                        </div>
                                        <div className="bar-wrapper-row">
                                          <div
                                            className="bar-fill"
                                            style={{
                                              width: `${barWidth}%`,
                                              backgroundColor: barColor,
                                              opacity: 0.2 + (barWidth / 100) * 0.6
                                            }}
                                          >
                                            <div className="bar-gradient" style={{
                                              background: `linear-gradient(90deg, transparent, ${barColor})`
                                            }}></div>
                                          </div>
                                          <div className="bar-label-row">
                                            <span className="percentage">{percentage.toFixed(1)}%</span>
                                            <span className="count">({item.count.toLocaleString()})</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  });
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Countries Distribution */}
                      {engagedDemographics.top_countries && engagedDemographics.top_countries.length > 0 && (
                        <div className="demographic-card" style={{ flex: 1 }}>
                          <h4 className="demographic-title">🌍 Paises</h4>
                          <CountriesBarChart
                            countries={engagedDemographics.top_countries.slice(0, 10)}
                            totalCount={engagedDemographics.top_countries.reduce((sum, c) => sum + c.count, 0)}
                            type="engaged"
                          />
                        </div>
                      )}
                    </div>

                    {/* Top Cities with Map */}
                    {engagedDemographics.top_cities && engagedDemographics.top_cities.length > 0 && (
                      <div className="demographic-card">
                        <h4 className="demographic-title">Ciudades</h4>

                        <div className="demographic-split-view">
                          {/* Left: List view */}
                          <div className="demographic-list-section">
                            <div className="cities-list">
                              {engagedDemographics.top_cities.slice(0, 10).map((item, index) => {
                                const total = engagedDemographics.top_cities.reduce((sum, c) => sum + c.count, 0);
                                const percentage = total > 0 ? (item.count / total) * 100 : 0;

                                return (
                                  <div key={index} className="city-item">
                                    <div className="city-rank">#{index + 1}</div>
                                    <div className="city-name">
                                       {item.city}
                                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '0.5rem' }}>
                                        ({item.count.toLocaleString()} personas comprometidas)
                                      </span>
                                    </div>
                                    <div className="city-percentage">{percentage.toFixed(1)}%</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Center: Cities Map view */}
                          <div className="demographic-map-section">
                            <DemographicsMapView
                              cities={engagedCitiesWithCoords}
                              totalCount={engagedDemographics.top_cities.reduce((sum, c) => sum + c.count, 0)}
                              type="engaged"
                              isLoading={loadingCoordinates}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Mensaje si no hay datos completos */}
                    {!engagedDemographics.has_complete_data && engagedDemographics.fetch_status === 'partial' && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fcd34d',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        color: '#92400e',
                        marginTop: '1rem'
                      }}>
                        ⚠️ Algunos datos de audiencia comprometida no están disponibles para esta cuenta.
                      </div>
                    )}
                  </>
                )}

                {!engagedDemographics && !loadingEngagedDemographics && !engagedDemographicsError && (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.5rem',
                    color: '#6b7280'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      No hay datos de audiencia comprometida disponibles.
                    </p>
                    {insights?.instagram_id && (
                      <button
                        onClick={() => loadEngagedDemographics(insights.instagram_id)}
                        style={{
                          marginTop: '1rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem',
                          backgroundColor: '#8b5cf6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        📊 Obtener Demografía Comprometida
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Footer */}
        <div className="insights-footer">
          {insights && (
            <>
              <button
                onClick={() => {
                  const instagramId = insights?.instagram_id;
                  if (instagramId) {
                    navigate(`/instagram/stories?instagram_id=${instagramId}`);
                  } else {
                    alert('No se pudo obtener el ID de Instagram. Por favor, intenta de nuevo.');
                  }
                }}
                className="btn-view-stories"
                disabled={!insights?.instagram_id}
                title={!insights?.instagram_id ? 'No se pudo obtener el ID de Instagram' : 'Ver métricas de stories'}
              >
                Stories
              </button>
              <button onClick={loadInstagramInsights} className="estado-btn">
                Actualizar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal de Comentarios */}
      {commentsModalOpen && (
        <div className="comments-modal-overlay" onClick={() => setCommentsModalOpen(false)}>
          <div className="comments-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="comments-modal-header">
              <div className="comments-modal-title">
                <span className="comments-modal-icon">💬</span>
                <h3>
                  Comentarios {commentsModalData?.filter === 'positive' && '😊 Positivos'}
                  {commentsModalData?.filter === 'neutral' && '😐 Neutrales'}
                  {commentsModalData?.filter === 'negative' && '😞 Negativos'}
                </h3>
              </div>
              <button className="comments-modal-close" onClick={() => setCommentsModalOpen(false)}>
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="comments-modal-content">
              {loadingComments ? (
                <Loading fullScreen={false} />
              ) : commentsModalData?.comments?.length > 0 ? (
                <>
                  <div className="comments-count">
                    Mostrando {commentsModalData.comments.length} de {commentsModalData.total_comments} comentarios
                  </div>
                  <div className="comments-list">
                    {commentsModalData.comments.map((comment, index) => (
                      <div key={comment.id || index} className="comment-item">
                        <div className="comment-header">
                          <span className="comment-username">@{comment.username}</span>
                          <span className="comment-date">
                            {comment.timestamp ? new Date(comment.timestamp).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <div className="comment-text">{comment.text}</div>
                        {comment.like_count > 0 && (
                          <div className="comment-likes">❤️ {comment.like_count}</div>
                        )}
                        <div className={`comment-sentiment ${comment.sentiment_label}`}>
                          {comment.sentiment_label === 'positive' && '😊 Positivo'}
                          {comment.sentiment_label === 'neutral' && '😐 Neutral'}
                          {comment.sentiment_label === 'negative' && '😞 Negativo'}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="comments-empty">
                  <p>No hay comentarios {commentsModalData?.filter !== 'all' ? `${commentsModalData?.filter}s` : ''} para mostrar</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="comments-modal-footer">
              <button className="btn-close-comments" onClick={() => setCommentsModalOpen(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerModalInstagramDetalle;
